interface ElementData {
  tagName: string;
  className: string;
  id: string;
  text: string | null;
  href?: string | null;
  type?: string | null;
  value?: string | null;
  checked?: boolean;
  selected?: boolean;
  attributes: Record<string, string>;
  path: string[];
}

/**
 * Extracts detailed information about an HTML element including its attributes,
 * DOM path, and element-specific properties.
 *
 * @param element - The HTML element to analyze
 * @returns An ElementData object containing the element's properties
 */
export function getElementData(element: HTMLElement): ElementData {
  // Get DOM path
  const path: string[] = [];
  let currentElement: HTMLElement | null = element;
  while (currentElement && currentElement !== document.body) {
    let selector = currentElement.tagName.toLowerCase();
    if (currentElement.id) {
      selector += `#${currentElement.id}`;
    } else if (
      currentElement.classList &&
      currentElement.classList.length > 0
    ) {
      // Handle both string className and DOMTokenList cases
      selector += `.${Array.from(currentElement.classList).join(".")}`;
    }
    path.unshift(selector);
    currentElement = currentElement.parentElement;
  }

  // Get all attributes
  const attributes: Record<string, string> = {};
  Array.from(element.attributes).forEach((attr) => {
    attributes[attr.name] = attr.value;
  });

  const data: ElementData = {
    tagName: element.tagName,
    className: element.className,
    id: element.id,
    text: element.textContent?.trim() ?? null,
    attributes,
    path,
  };

  // Add specific properties based on element type
  if (element instanceof HTMLAnchorElement) {
    data.href = element.href;
  }
  if (element instanceof HTMLInputElement) {
    data.type = element.type;
    data.value = element.type === "password" ? "[REDACTED]" : element.value;
    if (element.type === "checkbox" || element.type === "radio") {
      data.checked = element.checked;
    }
  }
  if (element instanceof HTMLSelectElement) {
    data.value = element.value;
    data.selected = element.selectedIndex !== -1;
  }

  return data;
}
